<template>
  <section id="hero" class="relative min-h-screen flex items-center justify-center overflow-hidden">
    <!-- Animated Background -->
    <div class="absolute inset-0 z-0">
      <div class="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-primary-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900"></div>
      <div class="absolute inset-0 opacity-30">
        <div
          v-for="i in 6"
          :key="i"
          class="absolute rounded-full mix-blend-multiply dark:mix-blend-screen animate-float"
          :class="[
            i % 2 === 0 ? 'bg-primary-300 dark:bg-primary-600' : 'bg-purple-300 dark:bg-purple-600',
            `w-${20 + i * 10} h-${20 + i * 10}`
          ]"
          :style="{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${i * 0.5}s`,
            animationDuration: `${3 + i * 0.5}s`
          }"
        ></div>
      </div>
    </div>

    <!-- Content -->
    <div class="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div class="max-w-4xl mx-auto">
        <!-- Greeting -->
        <h1 class="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-4 animate-slide-up">
          <span class="block text-lg sm:text-xl lg:text-2xl font-medium text-gray-600 dark:text-gray-300 mb-2">
            {{ $t('hero.greeting') }}
          </span>
          <span class="text-gradient" :class="{ 'khmer': isKhmer }">
            {{ $t('hero.name') }}
          </span>
        </h1>

        <!-- Title -->
        <h2 class="text-2xl sm:text-3xl lg:text-4xl font-semibold text-gray-700 dark:text-gray-200 mb-6 animate-slide-up" 
            style="animation-delay: 0.2s"
            :class="{ 'khmer': isKhmer }">
          {{ $t('hero.title') }}
        </h2>

        <!-- Description -->
        <p class="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed animate-slide-up" 
           style="animation-delay: 0.4s"
           :class="{ 'khmer': isKhmer }">
          {{ $t('hero.description') }}
        </p>

        <!-- CTA Buttons -->
        <div class="flex flex-col sm:flex-row gap-4 justify-center items-center animate-slide-up" style="animation-delay: 0.6s">
          <button
            @click="scrollToContact"
            class="btn-primary w-full sm:w-auto flex justify-center items-center min-w-[200px] group"
          >
            <span>{{ $t('hero.cta') }}</span>
            <ArrowRightIcon class="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
          </button>
          
          <button
            @click="downloadCV"
            class="btn-secondary w-full sm:w-auto flex justify-center items-center min-w-[200px] group"
          >
            <DocumentArrowDownIcon class="w-5 h-5 mr-2 transition-transform group-hover:translate-x-1" />
            <span>{{ $t('hero.downloadCV') }}</span>
          </button>
        </div>

        <!-- Social Links -->
        <div class="flex justify-center space-x-6 mt-12 animate-slide-up" style="animation-delay: 0.8s">
          <a
            v-for="social in socialLinks"
            :key="social.name"
            :href="social.url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
            :title="social.name"
          >
            <component :is="social.icon" class="w-6 h-6" />
          </a>
        </div>

        <!-- Scroll Indicator -->
        <div class="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDownIcon class="w-6 h-6 text-gray-400 dark:text-gray-600" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import {
  ArrowRightIcon,
  DocumentArrowDownIcon,
  // ChevronDownIcon
} from '@heroicons/vue/24/outline'

// Social icons (simplified for this example)
const GitHubIcon = {
  template: `
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.374 0 0 5.373 0 12 0 17.302 3.438 21.8 8.207 23.387c.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
    </svg>
  `
}

const LinkedInIcon = {
  template: `
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  `
}

const TwitterIcon = {
  template: `
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
    </svg>
  `
}

const store = useStore()

const isKhmer = computed(() => store.getters.isKhmer)

const socialLinks = [
  {
    name: 'GitHub',
    url: 'https://github.com/karonasrun',
    icon: GitHubIcon
  },
  {
    name: 'LinkedIn',
    url: 'https://linkedin.com/in/karonasrun',
    icon: LinkedInIcon
  },
  {
    name: 'Twitter',
    url: 'https://twitter.com/karonasrun',
    icon: TwitterIcon
  }
]

const scrollToContact = () => {
  const contactSection = document.getElementById('contact')
  if (contactSection) {
    contactSection.scrollIntoView({ behavior: 'smooth' })
  }
}

const downloadCV = () => {
  // This would typically download a CV file
  // For demo purposes, we'll just show an alert
  alert('CV download functionality would be implemented here')
}
</script>

<style scoped>
@keyframes float {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  33% {
    transform: translateY(-10px) rotate(120deg);
  }
  66% {
    transform: translateY(5px) rotate(240deg);
  }
}

.animate-float {
  animation: float linear infinite;
}
</style> 